<template>
  <vx-card class="mt-base">
    <h4 class="mb-5">
      <strong>3. Texture</strong> is the natural shape or pattern of the strands of hair. There are
      four main categories of hair texture: straight, wavy, curly, and coily.
    </h4>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/hairdressing/activity_31.png')"
      classes="xl:w-1/2 lg:w-2/3 w-full"
      alt="Four circles showing hair texture patterns: straight lines, wavy S-shaped lines, curly looped lines, and tightly zig-zagged coily lines."
    />

    <h4 class="mb-5">Take the Texture Test: What Texture Is Your Hair?</h4>

    <p class="mb-5">
      The best way to assess which category your hair belongs to is to leave your hair free from
      products, wash it, and then leave it to air dry.
    </p>
    <ul class="default-list">
      <li>
        If your hair dries without bends or curling, then it’s <strong>straight</strong>. This is
        also called Type 1 hair.
      </li>
      <li>
        If your hair dries in an ‘S’ shape or with a slight curve, then it is categorised as
        <strong>wavy</strong>. This is also called Type 2 hair.
      </li>
      <li>
        If the hair dries with defined curls or loops, then it’s <strong>curly</strong>. This is
        also called Type 3 hair.
      </li>
      <li>
        If the hair dries in tight curls, zig-zag patterns, or spirals, it is classified as
        <strong>coily</strong>. This is also called Type 4 hair.
      </li>
    </ul>

    <p class="mb-5">
      There are also degrees of how straight, wavy, curly, or curly hair can be. Take a look at the
      12 Major Hair Types image below to help you categorise your hair.
    </p>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/hairdressing/activity_32.png')"
      classes="xl:w-1/2 lg:w-2/3 w-full"
      alt="The 12 major hair types infographic: photos of women grouped by texture, from loosest to tightest. Straight: 1a, 1b, 1c. Wavy: 2a, 2b, 2c. Curly: 3a, 3b, 3c. Coily: 4a, 4b, 4c."
    >
      <p class="mb-5">
        <a
          href="https://www.cosmopolitan.com/style-beauty/beauty/a31956130/hair-types/"
          target="_blank"
          >https://www.cosmopolitan.com/style-beauty/beauty/a31956130/hair-types/</a
        >
      </p>
    </centered-image-wrapper>

    <p class="mb-5">
      Categorise your hair according to which of the 12 types above it best fits (e.g., from 1a to
      4c). Record your answer in the space below:
    </p>
    <div v-for="(question, index) in questions" :key="index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <div class="vx-row my-5">
        <div class="vx-col w-full mb-2" v-if="isEditable">
          <select
            class="default-select"
            v-model="answers['question' + index]"
            :aria-label="question.question"
          >
            <option v-for="answer in question.answers" :key="answer.val" :value="answer.val">
              {{ answer.answer }}
            </option>
          </select>
        </div>
        <div class="my-2 mx-5" v-else>
          <p
            class="my-2"
            v-for="answer in question.answers"
            :key="answer.val"
            v-if="answers['question' + index] != 0"
          >
            <span v-if="answers['question' + index] == answer.val">{{ answer.answer }}</span>
          </p>
          <p class="my-2" v-else>N/A</p>
        </div>
      </div>
    </div>

    <h4 class="mb-5">Texture: What Does This Mean for Hair Treatment?</h4>

    <p class="mb-5"><strong>For straight hair:</strong></p>

    <ul class="default-list">
      <li>Wash alternate days with a sulfate-free shampoo.</li>
      <li>Create lift with texturising spray.</li>
      <li>Blow-dry upwards from roots.</li>
      <li>Finish blow dry with a blast of cooler air.</li>
      <li>Apply conditioner to ends of the hair first, comb upwards with fingers.</li>
    </ul>
    <p class="mb-5"><strong>For wavy hair:</strong></p>
    <ul class="default-list">
      <li>Deep condition regularly.</li>
      <li>Air dry hair for a natural look.</li>
      <li>Keep well moisturised.</li>
      <li>Comb with fingers to maintain definition.</li>
      <li>Use hairpins to keep the shape.</li>
    </ul>

    <p class="mb-5"><strong>For curly hair:</strong></p>
    <ul class="default-list">
      <li>Wash once or twice a week - don’t overwash.</li>
      <li>Use a hydrating conditioner.</li>
      <li>Apply products when hair is still damp.</li>
      <li>Use a diffuser attachment when blowdrying.</li>
      <li>Avoid products with sulphates.</li>
    </ul>
    <p class="mb-5"><strong>For coily hair:</strong></p>
    <ul class="default-list">
      <li>Use a water-based leave-in conditioner.</li>
      <li>Shampoo less often - bubbles are the enemy!</li>
      <li>Cut hair as it is worn rather than trying to stretch, straighten, or wet first.</li>
      <li>Choose products that moisturise.</li>
      <li>Air dry as much as possible rather than blow-dry.</li>
    </ul>

    <save-buttons
      :is-editable="isEditable"
      @save="save"
      :student-back="
        '/student/work-experience/' + this.$route.params.id + '/activities/identifying-hair-types'
      "
      :teacher-back="
        '/teacher/vje/experience/view/' +
        this.$route.params.id +
        '/activities/identifying-hair-types'
      "
    />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      questions: {
        1: {
          question: 'My hair texture is:',
          answers: [
            { val: 0, answer: 'Select your hair texture' },
            { val: 1, answer: 'Straight' },
            { val: 2, answer: 'Wavy' },
            { val: 3, answer: 'Curly' },
            { val: 4, answer: 'Coily' },
          ],
        },
      },
      answers: {
        question1: 0,
      },
    };
  },
  methods: {
    save() {
      this.data = this.answers;
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (this.data.question1) {
      this.answers = this.data;
    }
  },
};
</script>
